import React, { useMemo, useState } from 'react';
import { Box, ButtonBase, Chip, LinearProgress, Link, Tab, Tabs, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { ZERO_ICONS } from '../../constants/zeroIcons';
import { useNumberFormat } from '../../hooks/useNumberFormat';
import { useBranchMetrics, useCollectionSites } from '../../hooks/useOperationsData';
import { contentBorders } from '../../theme/createContentTheme';
import type { BranchOperationalRow, ContainerMapItem } from '../../types/dashboard.type';
import type { CollectionSiteSchema } from '../../types/collection-site.type';
import {
  capacityInKg,
  containerAlarms,
  containerTone,
  fillShare,
  isAwaitingPickup,
  isOutOfService,
  needsAttention,
  sortContainersBySeverity,
  type ContainerAlarm,
} from '../../utils/containers.helpers';
import { siteDisplayName } from '../../utils/esgByBranch.helpers';
import { toLngLat, type LngLat } from '../../utils/geo';
import EmptyState from '../EmptyState';
import KpiFigure from '../metrics/KpiFigure';
import MetricStrip from '../metrics/MetricStrip';
import { LazyZeroMap, type MapMarker, type MapPoint } from '../map';
import CommandSurface from './CommandSurface';
import StatusChip from './StatusChip';

type Filter = 'all' | 'attention' | 'awaiting' | 'outOfService';
type PanelTab = 'attention' | 'branches' | 'containers';
type Selection = { kind: 'container' | 'branch'; id: string } | null;

const ALARM_ICON: Record<ContainerAlarm, typeof ZERO_ICONS.alarmFire> = {
  fire: ZERO_ICONS.alarmFire,
  full: ZERO_ICONS.alarmFull,
  tilt: ZERO_ICONS.alarmTilt,
  battery: ZERO_ICONS.alarmBattery,
};

interface SiteCommandSurfaceProps {
  orgId: string;
  containers: ContainerMapItem[];
  /** Month value for branch metrics (`yyyy-MM-ddT00:00:00Z`). */
  monthValue: string;
  /** Branch month metrics are a Waste Outlet feature (`esg_report.read`). */
  canBranchMetrics: boolean;
}

interface SiteRow {
  site: CollectionSiteSchema;
  name: string;
  position: LngLat | null;
}

/**
 * Sites & containers command surface for waste outlets and charities: where
 * branches and containers are, which containers need attention, and — for
 * branches — this month's collected mass. Positions are registered locations,
 * not live tracking.
 */
const SiteCommandSurface: React.FC<SiteCommandSurfaceProps> = ({ orgId, containers, monthValue, canBranchMetrics }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const fmt = useNumberFormat();
  const borders = contentBorders(theme);
  const [filter, setFilter] = useState<Filter>('all');
  const [showBranches, setShowBranches] = useState(true);
  const [showContainers, setShowContainers] = useState(true);
  // Branches first (where the organisation operates), then containers, then attention.
  const [tab, setTab] = useState<PanelTab>('branches');
  const [selection, setSelection] = useState<Selection>(null);

  const sitesQuery = useCollectionSites(orgId, true);
  const branchesQuery = useBranchMetrics(orgId, monthValue, canBranchMetrics && (tab === 'branches' || selection?.kind === 'branch'));

  const sites: SiteRow[] = useMemo(
    () =>
      sitesQuery.data.map((site) => ({
        site,
        name: siteDisplayName(site) ?? t('branchAnalysis.unnamedBranch'),
        position: toLngLat(site.latitude, site.longitude),
      })),
    [sitesQuery.data, t],
  );
  const branchMetrics = useMemo(() => {
    const map = new Map<string, BranchOperationalRow>();
    for (const row of branchesQuery.data?.branches ?? []) map.set(row.collection_site_id, row);
    return map;
  }, [branchesQuery.data]);

  const filtered = useMemo(() => {
    const matches = (c: ContainerMapItem) =>
      filter === 'all' ||
      (filter === 'attention' && needsAttention(c)) ||
      (filter === 'awaiting' && isAwaitingPickup(c)) ||
      (filter === 'outOfService' && isOutOfService(c));
    return sortContainersBySeverity(containers.filter(matches));
  }, [containers, filter]);
  const attention = useMemo(() => sortContainersBySeverity(containers.filter(needsAttention)), [containers]);
  const located = (c: ContainerMapItem) => toLngLat(c.latitude, c.longitude);
  const unlocatedCount = containers.filter((c) => !located(c)).length;

  const containerLabel = (c: ContainerMapItem) => c.container_name || t('dashboard.site.unnamedContainer');
  /** The most severe alarm names the state (a red "Empty" chip would contradict itself). */
  const statusLabel = (c: ContainerMapItem) => {
    const [alarm] = containerAlarms(c);
    return alarm ? t(`alarms.${alarm}`) : t(`status.container.${c.status}`, { defaultValue: c.status });
  };

  // ---- map features ---------------------------------------------------------------
  const points: MapPoint[] = showContainers
    ? filtered.flatMap((c) => {
        const position = located(c);
        if (!position) return [];
        const selected = selection?.kind === 'container' && selection.id === c.container_id;
        return [{ id: `container:${c.container_id}`, position, tone: containerTone(c), state: selected ? 'selected' : 'default' }];
      })
    : [];
  const markers: MapMarker[] = showBranches
    ? sites.flatMap((row) => {
        if (!row.position) return [];
        const selected = selection?.kind === 'branch' && selection.id === row.site.id;
        return [
          {
            id: `branch:${row.site.id}`,
            position: row.position,
            tone: selected ? 'active' : 'complete',
            label: `${t('dashboard.site.branch')}: ${row.name}`,
            glyph: 'branch',
            shape: 'square',
            state: selected ? 'selected' : 'default',
            raised: selected,
          } satisfies MapMarker,
        ];
      })
    : [];

  const selectedContainer = selection?.kind === 'container' ? containers.find((c) => c.container_id === selection.id) : undefined;
  const selectedSite = selection?.kind === 'branch' ? sites.find((s) => s.site.id === selection.id) : undefined;
  const selectedPosition = selectedContainer ? located(selectedContainer) : selectedSite?.position ?? null;

  const select = (next: Selection) => setSelection(next);
  const onMapSelect = (id: string) => {
    const [kind, raw] = id.split(':');
    if (kind === 'container' || kind === 'branch') select({ kind, id: raw });
  };

  // ---- toolbar ------------------------------------------------------------------------
  const filterChip = (value: Filter, label: string, count?: number) => (
    <Chip
      key={value}
      size="small"
      label={count === undefined ? label : `${label} · ${fmt.number(count)}`}
      onClick={() => setFilter(value)}
      variant={filter === value ? 'filled' : 'outlined'}
      color={filter === value ? 'primary' : 'default'}
      aria-pressed={filter === value}
      sx={{ flexShrink: 0 }}
    />
  );
  const layerChip = (on: boolean, toggle: () => void, label: string, Icon: typeof ZERO_ICONS.branch) => (
    <Chip
      size="small"
      icon={<Icon sx={{ fontSize: 16 }} />}
      label={label}
      onClick={toggle}
      variant={on ? 'filled' : 'outlined'}
      aria-pressed={on}
      sx={{ flexShrink: 0, bgcolor: on ? 'action.selected' : undefined }}
    />
  );
  const toolbar = (
    <>
      {filterChip('all', t('dashboard.site.filters.all'), containers.length)}
      {filterChip('attention', t('dashboard.site.filters.attention'), attention.length)}
      {filterChip('awaiting', t('dashboard.site.filters.awaiting'), containers.filter(isAwaitingPickup).length)}
      {filterChip('outOfService', t('dashboard.site.filters.outOfService'), containers.filter(isOutOfService).length)}
      <Box sx={{ flex: { md: 1 }, minWidth: 8 }} />
      {sitesQuery.status !== 'forbidden' && sites.length > 0
        ? layerChip(showBranches, () => setShowBranches((v) => !v), t('dashboard.site.layers.branches'), ZERO_ICONS.branch)
        : null}
      {layerChip(showContainers, () => setShowContainers((v) => !v), t('dashboard.site.layers.containers'), ZERO_ICONS.container)}
    </>
  );

  // ---- panel lists ----------------------------------------------------------------------
  const rowSx = (active: boolean) => ({
    width: '100%',
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) auto',
    alignItems: 'center',
    columnGap: 1.5,
    px: 2,
    py: 1.5,
    textAlign: 'start' as const,
    borderBottom: `1px solid ${borders.subtle}`,
    bgcolor: active ? 'action.selected' : 'transparent',
    '&:hover': { bgcolor: active ? 'action.selected' : 'action.hover' },
  });

  const containerRow = (c: ContainerMapItem) => {
    const alarms = containerAlarms(c);
    const share = fillShare(c);
    return (
      <Box component="li" key={c.container_id}>
        <ButtonBase onClick={() => select({ kind: 'container', id: c.container_id })} sx={rowSx(selection?.id === c.container_id)}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
              {containerLabel(c)}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, color: 'text.secondary', flexWrap: 'wrap' }}>
              {alarms.map((a) => {
                const Icon = ALARM_ICON[a];
                return (
                  <Box key={a} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
                    <Icon aria-hidden sx={{ fontSize: 15, color: a === 'fire' ? 'error.main' : 'warning.main' }} />
                    <Typography variant="caption">{t(`alarms.${a}`)}</Typography>
                  </Box>
                );
              })}
              {share !== null ? (
                <Typography variant="caption">
                  <bdi>{fmt.share(share)}</bdi> {t('dashboard.site.full')}
                </Typography>
              ) : null}
              {!located(c) ? <Typography variant="caption">{t('dashboard.site.noPosition')}</Typography> : null}
            </Box>
          </Box>
          <StatusChip tone={containerTone(c)} label={statusLabel(c)} />
        </ButtonBase>
      </Box>
    );
  };

  const listOf = (items: React.ReactNode[], empty: React.ReactNode) =>
    items.length > 0 ? (
      <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
        {items}
      </Box>
    ) : (
      <Box sx={{ p: 2 }}>{empty}</Box>
    );

  const branchRows = sites.map((row) => {
    const metrics = branchMetrics.get(row.site.id);
    return (
      <Box component="li" key={row.site.id}>
        <ButtonBase onClick={() => select({ kind: 'branch', id: row.site.id })} sx={rowSx(selection?.id === row.site.id)}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
            <ZERO_ICONS.branch aria-hidden sx={{ fontSize: 18, color: 'text.secondary', flexShrink: 0 }} />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                {row.name}
              </Typography>
              {!row.position ? (
                <Typography variant="caption" color="text.secondary">
                  {t('branchAnalysis.locationNotSet')}
                </Typography>
              ) : null}
            </Box>
          </Box>
          {metrics ? (
            <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
              <bdi>{fmt.withUnit('mass_kg', metrics.total_waste_kg, { scaleMass: true })}</bdi>
            </Typography>
          ) : null}
        </ButtonBase>
      </Box>
    );
  });

  const tabCount = (n: number) => (
    <Box component="span" sx={{ color: 'text.secondary', fontWeight: 500, marginInlineStart: 0.75 }}>
      <bdi>{fmt.number(n)}</bdi>
    </Box>
  );
  const branchesVisible = sitesQuery.status !== 'forbidden' && sites.length > 0;
  const activeTab: PanelTab = tab === 'branches' && !branchesVisible ? 'containers' : tab;

  const panel = (
    <>
      <Tabs
        value={activeTab}
        onChange={(_, v: PanelTab) => setTab(v)}
        variant="fullWidth"
        sx={{ borderBottom: `1px solid ${borders.default}`, minHeight: 44, '& .MuiTab-root': { mx: 0, px: 1.5 } }}
      >
        {branchesVisible ? (
          <Tab value="branches" label={<span>{t('dashboard.site.tabs.branches')}{tabCount(sites.length)}</span>} />
        ) : null}
        <Tab value="containers" label={<span>{t('dashboard.site.tabs.containers')}{tabCount(filtered.length)}</span>} />
        <Tab value="attention" label={<span>{t('dashboard.site.tabs.attention')}{tabCount(attention.length)}</span>} />
      </Tabs>
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        {activeTab === 'attention'
          ? listOf(
              attention.map(containerRow),
              <EmptyState
                variant="inline"
                icon={ZERO_ICONS.completed}
                title={t('dashboard.site.empty.attentionTitle')}
                description={t('dashboard.site.empty.attentionDescription')}
                minHeight={180}
              />,
            )
          : activeTab === 'branches'
            ? listOf(branchRows, null)
            : listOf(
                filtered.map(containerRow),
                <EmptyState variant="inline" icon={ZERO_ICONS.container} title={t('dashboard.site.empty.filtered')} minHeight={180} />,
              )}
        {unlocatedCount > 0 && activeTab !== 'branches' ? (
          <Typography variant="caption" component="p" color="text.secondary" sx={{ px: 2, py: 1.25 }}>
            {t('dashboard.site.unlocated', { count: unlocatedCount })}
          </Typography>
        ) : null}
      </Box>
    </>
  );

  // ---- detail -----------------------------------------------------------------------------
  let detail: React.ReactNode = null;
  let detailTitle: React.ReactNode = null;
  if (selectedContainer) {
    const c = selectedContainer;
    const share = fillShare(c);
    const capacity = capacityInKg(c.capacity, c.capacity_unit);
    const alarms = containerAlarms(c);
    detailTitle = containerLabel(c);
    detail = (
      <Box sx={{ p: 2, display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 2.5 }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <StatusChip tone={containerTone(c)} label={statusLabel(c)} size="md" />
        </Box>
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
            {t('dashboard.site.fillLevel')}
          </Typography>
          <Typography variant="h5" component="p" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
            <bdi>{fmt.withUnit('mass_kg', c.fill_level)}</bdi>
            {capacity !== null ? (
              <Typography component="span" variant="body2" color="text.secondary" sx={{ marginInlineStart: 0.75 }}>
                {t('dashboard.site.ofCapacity', { capacity: fmt.withUnit('mass_kg', capacity, { scaleMass: true }) })}
              </Typography>
            ) : null}
          </Typography>
          {share !== null ? (
            <LinearProgress
              variant="determinate"
              value={Math.min(share * 100, 100)}
              color={share >= 0.9 ? 'warning' : 'primary'}
              sx={{ mt: 1 }}
              aria-label={t('dashboard.site.fillLevel')}
            />
          ) : (
            <Typography variant="caption" color="text.secondary">
              {t('dashboard.site.capacityUnknown')}
            </Typography>
          )}
        </Box>
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
            {t('dashboard.site.alarms')}
          </Typography>
          {alarms.length === 0 ? (
            <Typography variant="body2">{t('dashboard.site.noAlarms')}</Typography>
          ) : (
            <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'grid', gap: 1 }}>
              {alarms.map((a) => {
                const Icon = ALARM_ICON[a];
                return (
                  <Box component="li" key={a} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Icon aria-hidden sx={{ fontSize: 18, color: a === 'fire' ? 'error.main' : 'warning.main' }} />
                    <Typography variant="body2">{t(`alarms.${a}`)}</Typography>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
        {!located(c) ? (
          <Typography variant="caption" color="text.secondary">
            {t('dashboard.site.noPosition')}
          </Typography>
        ) : null}
      </Box>
    );
  } else if (selectedSite) {
    const metrics = branchMetrics.get(selectedSite.site.id);
    const phone = (selectedSite.site.data?.contact_info as { phone?: string } | undefined)?.phone;
    detailTitle = selectedSite.name;
    detail = (
      <Box sx={{ p: 2, display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 2 }}>
        {phone ? (
          <Typography variant="body2" color="text.secondary">
            <bdi dir="ltr">{phone}</bdi>
          </Typography>
        ) : null}
        {canBranchMetrics ? (
          branchesQuery.status === 'loading' ? (
            <LinearProgress aria-label={t('common.loading')} />
          ) : metrics ? (
            <MetricStrip columns={{ xs: 1, sm: 3, md: 1 }} dense>
              <KpiFigure icon={ZERO_ICONS.mass} label={t('dashboard.stats.completed_waste_amount')} value={fmt.metric('mass_kg', metrics.total_waste_kg, { scaleMass: true }).text} unit={fmt.metric('mass_kg', metrics.total_waste_kg, { scaleMass: true }).unit} size="sm" />
              <KpiFigure icon={ZERO_ICONS.rate} label={t('dashboard.stats.waste_diversion_rate')} value={fmt.number(metrics.waste_diversion_rate_percent, { maximumFractionDigits: 1 })} unit="%" size="sm" />
              <KpiFigure icon={ZERO_ICONS.sensor} label={t('dashboard.site.fillReports')} value={fmt.number(metrics.fill_level_report_count)} size="sm" />
            </MetricStrip>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {t('dashboard.site.noBranchActivity')}
            </Typography>
          )
        ) : null}
        <Link component={RouterLink} to="/branch-analysis" variant="body2" sx={{ fontWeight: 600, justifySelf: 'start' }}>
          {t('dashboard.site.openBranchAnalysis')}
        </Link>
      </Box>
    );
  }

  const fitTo = selectedPosition ? [selectedPosition] : undefined;
  const fitKey = `${filter}|${showBranches}|${showContainers}|${points.length}|${markers.length}|${selection?.id ?? ''}`;

  return (
    <CommandSurface
      ariaLabel={t('dashboard.site.title')}
      toolbar={toolbar}
      map={
        <LazyZeroMap
          ariaLabel={t('map.aria.site')}
          markers={markers}
          points={points}
          onSelect={onMapSelect}
          fitKey={fitKey}
          fitTo={fitTo}
          sx={{ position: 'absolute', inset: 0 }}
        />
      }
      panel={panel}
      detail={detail}
      detailTitle={detailTitle}
      onCloseDetail={() => setSelection(null)}
    />
  );
};

export default SiteCommandSurface;
